/**
 * Whether an incident task is operational work or management work.
 *
 * Mirrors `services/coms/src/routes/ICS/actionClassification.ts`, and reuses
 * the same three values as incident objectives so the two cannot drift. Stored
 * as `Management`, shown to users as "Managerial" — see
 * [incidentObjectiveTypes.ts](./incidentObjectiveTypes.ts).
 */

export const ACTION_CLASSIFICATION_OPERATIONAL = 'Operational';
export const ACTION_CLASSIFICATION_MANAGEMENT = 'Management';
export const ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL =
  'OperationalAndManagerial';

export const ACTION_CLASSIFICATIONS = [
  ACTION_CLASSIFICATION_OPERATIONAL,
  ACTION_CLASSIFICATION_MANAGEMENT,
  ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL,
] as const;

export type ActionClassification = (typeof ACTION_CLASSIFICATIONS)[number];

export const DEFAULT_ACTION_CLASSIFICATION: ActionClassification =
  ACTION_CLASSIFICATION_MANAGEMENT;

export const actionClassificationSegments = [
  {
    value: ACTION_CLASSIFICATION_OPERATIONAL,
    abbrev: 'O',
    tooltip: 'Operational',
  },
  {
    value: ACTION_CLASSIFICATION_MANAGEMENT,
    abbrev: 'M',
    tooltip: 'Managerial',
  },
  {
    value: ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL,
    abbrev: 'O&M',
    tooltip: 'Operational & Managerial',
  },
] as const;

/** Dropdown options, using the display wording rather than the stored value. */
export const ACTION_CLASSIFICATION_OPTIONS = actionClassificationSegments.map(
  (segment) => ({ label: segment.tooltip, value: segment.value })
);

/**
 * Shown under the control, because marking a task Operational has a consequence
 * the drawer cannot otherwise show: it writes a row into the ICS-215, which
 * then appears on that work group's ICS-204.
 */
export const ACTION_CLASSIFICATION_HINT =
  'Managerial tasks print on the ICS-233. Operational tasks appear on the ICS-215 worksheet, and on the ICS-204 for the assigned ICS position.';

export function normalizeActionClassification(
  value: string | undefined | null
): ActionClassification {
  if (value === ACTION_CLASSIFICATION_OPERATIONAL) {
    return ACTION_CLASSIFICATION_OPERATIONAL;
  }
  if (value === ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL) {
    return ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL;
  }
  return DEFAULT_ACTION_CLASSIFICATION;
}

/** The `O` / `M` / `O&M` badge text, or undefined when the value is unknown. */
export function actionClassificationAbbrev(
  value: string | undefined | null
): string {
  const normalized = normalizeActionClassification(value);
  return (
    actionClassificationSegments.find((segment) => segment.value === normalized)
      ?.abbrev ?? 'M'
  );
}

/** Operational and O&M tasks appear on the ICS-215, and thus the ICS-204. */
export function isOperationalClassification(
  value: string | undefined | null
): boolean {
  const normalized = normalizeActionClassification(value);
  return (
    normalized === ACTION_CLASSIFICATION_OPERATIONAL ||
    normalized === ACTION_CLASSIFICATION_OPERATIONAL_AND_MANAGERIAL
  );
}
